import './Signup.css';
import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form, Checkbox } from 'antd';
import Button from '@views/Button';
import Link from 'redux-first-router-link';
import Metas from '@views/Metas';
import Input from '@views/Input';

import {
  CheckCircleFilled,
  EditOutlined,
  LockOutlined,
  PictureOutlined,
} from '@ant-design/icons';

export const SignupForm = ({
  username,
  usernameError,
  dniError,
  saving,
  onSubmit,
  checkUsername,
  clearUsernameUnavailableError,
  clearDniError,
}) => {
  const [form] = Form.useForm();
  const imageInputRef = React.useRef(null);
  const usernameFieldValue = Form.useWatch('username', form);
  const dniFieldValue = Form.useWatch('dni', form);
  const acceptTermsValue = Form.useWatch('acceptTerms', form);
  const [usernameFormatError, setUsernameFormatError] = React.useState(false);
  const [usernameChecking, setUsernameChecking] = React.useState(false);
  const [usernameAvailable, setUsernameAvailable] = React.useState(null);
  const [verifiedUsername, setVerifiedUsername] = React.useState('');
  const [profileImagePreview, setProfileImagePreview] = React.useState('');
  const [profileImageData, setProfileImageData] = React.useState('');
  const [profileImageError, setProfileImageError] = React.useState('');

  const openImagePicker = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const onImageSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isSupported = ['image/png', 'image/jpeg'].includes(file.type);
    if (!isSupported) {
      setProfileImageError('Formato inválido. Usá PNG o JPG.');
      setProfileImageData('');
      setProfileImagePreview('');
      return;
    }

    const maxSizeMB = 5;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setProfileImageError('La imagen supera los 5MB.');
      setProfileImageData('');
      setProfileImagePreview('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageBase64 = String(reader.result || '');
      setProfileImageError('');
      setProfileImagePreview(imageBase64);
      setProfileImageData(imageBase64);
    };
    reader.onerror = () => {
      setProfileImageError('No se pudo leer la imagen seleccionada.');
      setProfileImageData('');
      setProfileImagePreview('');
    };
    reader.readAsDataURL(file);
  };

  const onFinish = (fieldsValue) => {
    const { acceptTerms, ...restFields } = fieldsValue;
    onSubmit(
      profileImageData
        ? { ...restFields, profilePicture: profileImageData }
        : restFields
    );
  };

  const validateUsernameFormat = (e) => {
    const val = e.target.value;
    setUsernameFormatError(!!val && !/^[a-zA-Z0-9_]+$/.test(val));
  };

  const handleUsernameChange = (e) => {
    validateUsernameFormat(e);
    setUsernameAvailable(null);
    setVerifiedUsername('');
    clearUsernameUnavailableError();
  };

  const handleVerifyUsername = async () => {
    const rawValue = (usernameFieldValue || '').trim();
    if (!rawValue || rawValue.length < 4 || usernameFormatError) return;
    setUsernameChecking(true);
    const available = await checkUsername(rawValue);
    setUsernameChecking(false);
    setUsernameAvailable(available);
    setVerifiedUsername(rawValue);
  };

  const usernameIsVerifiedAndAvailable =
    usernameAvailable === true &&
    verifiedUsername &&
    verifiedUsername === (usernameFieldValue || '').trim();

  const canSubmit =
    !saving &&
    !dniError &&
    !usernameError &&
    !usernameFormatError &&
    usernameIsVerifiedAndAvailable &&
    !!acceptTermsValue &&
    !!usernameFieldValue &&
    usernameFieldValue.length >= 4 &&
    !!dniFieldValue &&
    dniFieldValue.length >= 6;
  return (
    <div className="SignupContainer">
      <Metas
        title={`Completá tus datos para participar del Prode Mundial 2026`}
        description="El juego de Página|12 para predecir los resultados del Mundial de Fútbol 2026"
      />
      <div className="hero-bg">
        <div>
          <h1>Configurá tu perfil</h1>
        </div>
      </div>
      <Form
        id="signup-form"
        spellCheck={false}
        layout="vertical"
        initialValues={{ username }}
        autoComplete="off"
        onFinish={onFinish}
        form={form}
      >
        <Row justify="center" className="signup-content-row">
          <Col span={24} md={10} lg={9} xl={8} id="profile-image-container">
            <div className="imagePickerContainer">
              <div className="avatar-circled">
                {profileImagePreview ? (
                  <img src={profileImagePreview} alt="Vista previa de perfil" />
                ) : (
                  <PictureOutlined className="avatar-placeholder-icon" />
                )}
              </div>
              <button
                type="button"
                className="edit-circle"
                onClick={openImagePicker}
                aria-label="Subir imagen de perfil"
              >
                <EditOutlined />
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="profile-image-input"
                style={{ display: 'none' }}
                tabIndex={-1}
                onChange={onImageSelected}
              />
            </div>
            <div className="profile-image-copy">
              <h2 className="profile-image-title">Imagen de perfil</h2>
              <p>
                Debe ser .PNG o .JPG no mayor a 5mb. Se recomienda que esté en formato cuadrado.
              </p>
            </div>
            {profileImageError ? <p className="danger">{profileImageError}</p> : null}
          </Col>
          <Col span={24} md={12} xl={8} xxl={8}>
            <h2>¿Cómo te querés llamar?</h2>
            <p>
              Este es el nombre con el cual figurarás en el ranking general.{' '}
              <b>No podrás modificarlo luego.</b>
            </p>
            <div className="signup-username-verify-row">
              <Form.Item name="username" className="signup-username-item">
                <Input
                  maxLength={20}
                  hasErrors={!!usernameError}
                  onChange={handleUsernameChange}
                />
              </Form.Item>
              <button
                type="button"
                className="signup-verify-btn"
                onClick={handleVerifyUsername}
                disabled={
                  usernameChecking ||
                  !usernameFieldValue ||
                  usernameFieldValue.trim().length < 4 ||
                  usernameFormatError
                }
              >
                {usernameChecking ? 'Verificando...' : 'Verificar'}
              </button>
              {usernameIsVerifiedAndAvailable ? (
                <CheckCircleFilled className="signup-username-ok-icon" />
              ) : null}
            </div>
            {usernameError ? (
              <p className="danger">
                El nombre ya está usado. Probá uno distinto.
              </p>
            ) : null}
            {usernameFormatError ? (
              <p className="danger">
                El nombre de usuario solo puede contener letras, números y _
              </p>
            ) : null}
            <hr style={{ marginBottom: 10 }} />
            <h2
              style={{
                display: 'inline-block',
                marginRight: 10,
                marginBottom: 0,
                marginTop: 20,
              }}
            >
              DNI
            </h2>
            <span>(sin puntos)</span>
            <Form.Item name="dni">
              <Input
                maxLength={12}
                pattern="[0-9]+"
                hasErrors={!!dniError}
                onKeyPress={() => clearDniError()}
              />
            </Form.Item>
            <p className="signup-dni-copy">
              <LockOutlined /> No compartiremos esta información
            </p>
            {dniError ? (
              <p className="danger">Ya existe una cuenta con el mismo DNI.</p>
            ) : null}
            <Form.Item
              name="acceptTerms"
              valuePropName="checked"
              className="signup-terms-item"
            >
              <Checkbox>
                Aceptar{' '}
                <Link to={['terms-and-conditions']}>
                  términos y condiciones
                </Link>
              </Checkbox>
            </Form.Item>
            <div className="signup-next-wrap">
              <Button
                htmlType="submit"
                disabled={!canSubmit}
                className="signup-next-btn"
              >
                Siguiente
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

SignupForm.displayName = 'Signup';
SignupForm.propTypes = {
  username: PropTypes.string,
  dni: PropTypes.string,
  usernameError: PropTypes.bool,
  dniError: PropTypes.bool,
  saving: PropTypes.bool,
  onSubmit: PropTypes.func,
  checkUsername: PropTypes.func,
  clearUsernameUnavailableError: PropTypes.func,
  clearDniError: PropTypes.func,
};
SignupForm.defaultProps = {
  username: '',
  dni: '',
  usernameError: false,
  dniError: false,
  saving: false,
  onSubmit: () => {},
  checkUsername: () => {},
  clearUsernameUnavailableError: () => {},
  clearDniError: () => {},
};

export default SignupForm;
